import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { AtlasDataService } from '../../../../../core/src';
import { AtlasAccountNotificationService } from '../../../account-notification.service';
import { SnackbarService } from '../../../core-components/snackbar/snackbar.service';
import {
  GetByAccountGroupIdSetting,
  SaveSetting,
  SectionContainer,
} from '../../../vbc_sdk/_internal/objects/notifications';
import { NotificationsService } from '../../../vbc_sdk/notifications.service';

const EXEC_REPORT_FLAG = 'executiveReportFlag';

enum CategoryState {
  On = 0,
  Off = 1,
  Indeterminate = 2,
}

enum Medium {
  GLOBAL,
  IN_APP,
  INSTANT_EMAIL,
  DIGEST_EMAIL,
  WEEKLY_EMAIL,
  MONTHLY_EMAIL,
}

interface MediumSection {
  id?: string;
  medium?: Medium;
}

interface Activity {
  id?: string;
  title?: string;
  description?: string;
  enabledMediums?: MediumSection[];
  tags?: Activity[];
}

interface PanelSection {
  id?: string;
  title?: string;
  activities?: Activity[];
}

interface GlobalSetting {
  id?: string;
  title?: string;
}

interface ReportMedium {
  id?: string;
  medium?: Medium;
}

interface ReportSetting {
  id?: string;
  description?: string;
  title?: string;
  mediums?: ReportMedium[];
}

class PanelSectionBuilder {
  private products: Map<string, PanelSection> = new Map<string, PanelSection>();

  addProduct(id: string, title: string): PanelSectionBuilder {
    const panel: PanelSection = this.products.get(id);
    if (!panel) {
      this.products.set(id, {
        id: id,
        title: title,
        activities: [],
      });
    } else {
      panel.title = title;
    }
    return this;
  }

  addActivity(productId: string, activityId: string, activityTitle: string): PanelSectionBuilder {
    const panel: PanelSection = this.products.get(productId);
    if (!panel) {
      console.log("Attempted to add an activity to a product that doesn't exist");
      return this;
    }

    const activity: Activity = panel.activities.find((a: Activity) => a.id === activityId);
    if (activity) {
      // We already have this activity
      return this;
    }

    panel.activities.push({
      id: activityId,
      title: activityTitle,
      enabledMediums: [],
      tags: [],
    });
  }

  addTag(
    productId: string,
    activityId: string,
    sectionId: string,
    tagId: string,
    tagTitle: string,
  ): PanelSectionBuilder {
    const panel: PanelSection = this.products.get(productId);
    if (!panel) {
      console.log("Attempted to add a tag to a product that doesn't exist");
      return this;
    }

    const activity: Activity = panel.activities.find((a: Activity) => a.id === activityId);
    if (!activity) {
      console.log("Attempted to add a tag to an activity that doesn't exist");
      return this;
    }

    const tag: Activity = activity.tags.find((a: Activity) => a.id === tagId);
    if (tag) {
      // We already have this tag
      return this;
    }

    activity.tags.push({
      id: tagId,
      title: tagTitle,
    });
  }

  enableMedium(sectionId: string, productId: string, activityId: string, medium: Medium): PanelSectionBuilder {
    const panel: PanelSection = this.products.get(productId);
    if (!panel) {
      console.log("Attempted to enable a medium to a product that doesn't exist");
      return this;
    }

    const activity: Activity = panel.activities.find((a: Activity) => a.id === activityId);
    if (!activity) {
      console.log("Attempted to enable a medium to an activity that doesn't exist");
      return this;
    }

    activity.enabledMediums.push({
      id: sectionId,
      medium: medium,
    });
  }

  build(): PanelSection[] {
    const result: PanelSection[] = [];
    this.products.forEach((section) => {
      result.push(section);
    });
    if (result.length === 0) {
      return null;
    }
    return result;
  }
}

@Component({
  selector: 'atlas-notification-accounts-form-settings',
  templateUrl: './accounts-form-settings.component.html',
  styleUrls: ['./accounts-form-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AccountsFormSettingsComponent {
  @Input() set accountGroupId(accountGroupId: string) {
    this.accountGroupId$$.next(accountGroupId);
  }

  get accountGroupId(): string {
    return this.accountGroupId$$.getValue();
  }

  @Input() set partnerId(partnerId: string) {
    this.partnerId$$.next(partnerId);
  }

  get partnerId(): string {
    return this.partnerId$$.getValue();
  }

  constructor(
    private notificationsService: NotificationsService,
    private dataService: AtlasDataService,
    private translate: TranslateService,
    private snackbarService: SnackbarService,
    private accountNotificationService: AtlasAccountNotificationService,
  ) {
    this.businessName$ = dataService.businessName$;
    this.address$ = dataService.address$;

    this.accountNotificationService.notificationSectionsErrors$
      .pipe(
        filter((err) => !!err),
        // Ignore Not Found errors, we'll simply hide the tab.
        filter((err: any) => !(err instanceof HttpErrorResponse && err.status === 404)),
      )
      .subscribe((err: any) => {
        this.error = true;
        this.onError.emit(err);
      });
  }

  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onError: EventEmitter<any> = new EventEmitter<any>();
  Medium = Medium;
  availableMediums = [Medium.INSTANT_EMAIL, Medium.DIGEST_EMAIL];
  reportMediums = [Medium.WEEKLY_EMAIL, Medium.MONTHLY_EMAIL];

  private accountGroupId$$ = new BehaviorSubject<string>(null);

  private partnerId$$ = new BehaviorSubject<string>(null);

  public saving = false;
  public error: boolean;
  checked: Map<string, boolean> = new Map<string, boolean>();
  rollbackCheckState: Map<string, boolean> = new Map<string, boolean>();
  public settingsByAccountGroupId$: Observable<GetByAccountGroupIdSetting[]> = combineLatest([
    this.partnerId$$,
    this.accountGroupId$$,
  ]).pipe(
    switchMap(([partnerId, accountGroupId]: [string, string]) =>
      this.notificationsService.getByAccountGroupId$(partnerId, accountGroupId).pipe(
        catchError((err) => {
          this.error = true;
          this.onError.emit(err);
          return of(null);
        }),
      ),
    ),
    filter((settings) => !!settings),
    map((resp) => (resp.data ? resp.data : [])),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public notificationsEnabled$: Observable<boolean> = combineLatest([
    this.dataService.userId$,
    this.settingsByAccountGroupId$,
  ]).pipe(
    map(
      ([userId, settings]: [string, GetByAccountGroupIdSetting[]]): GetByAccountGroupIdSetting =>
        settings.find((setting) => !!setting && setting.unifiedUserId === userId),
    ),
    map((setting: GetByAccountGroupIdSetting): boolean => (setting ? setting.notificationsEnabled || false : false)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public notificationSections$ = this.accountNotificationService.notificationSections$.pipe(
    filter((sections) => !!sections),
  );

  public globalSettings$: Observable<GlobalSetting[]> = this.notificationSections$.pipe(
    map((resp: SectionContainer[]) => {
      const result: GlobalSetting[] = [];
      for (const s of resp) {
        if (s.section.id === EXEC_REPORT_FLAG) {
          continue;
        }
        if (typeof s.section.value === 'boolean') {
          this.checked.set(this.checkboxId(s.section.id, s.section.id, Medium.GLOBAL), s.section.value);
        }
        result.push({
          id: s.section.id,
          title: s.section.localized_title,
        });
      }
      if (result.length === 0) {
        return null;
      }
      return result;
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public reportSettings$: Observable<ReportSetting[]> = this.notificationSections$.pipe(
    map((resp: SectionContainer[]) => {
      const result: ReportSetting[] = [];
      for (const s of resp) {
        if (s.section.id !== EXEC_REPORT_FLAG) {
          continue;
        }
        const mediums: ReportMedium[] = new Array<ReportMedium>(2);
        for (const a of s.products) {
          for (const ac of a.activities) {
            let medium = Medium.WEEKLY_EMAIL;
            let arrayIdx = 0;
            if (ac.id.toUpperCase().indexOf('MONTHLY') >= 0) {
              medium = Medium.MONTHLY_EMAIL;
              arrayIdx = 1;
            }
            mediums[arrayIdx] = {
              id: ac.id,
              medium: medium,
            };
            this.checked.set(this.checkboxId(ac.id, s.section.id, medium), ac.value);
          }
        }
        result.push({
          id: s.section.id,
          title: s.section.localized_title,
          mediums: mediums,
        });
      }
      if (result.length === 0) {
        return null;
      }
      return result;
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public panelSections$: Observable<PanelSection[]> = this.notificationSections$.pipe(
    map((resp: SectionContainer[]) => {
      const builder: PanelSectionBuilder = new PanelSectionBuilder();

      for (const s of resp) {
        if (s.section.id === EXEC_REPORT_FLAG) {
          continue;
        }
        const medium = this.getMediumFromId(s.section.id);
        for (const p of s.products) {
          builder.addProduct(p.productId, p.title);
          for (const a of p.activities) {
            builder.addActivity(p.productId, a.id, a.localized_title);

            if (medium) {
              builder.enableMedium(s.section.id, p.productId, a.id, medium);
            }
            this.checked.set(this.checkboxId(a.id, s.section.id, medium), a.value);

            for (const t of a.tags) {
              builder.addTag(p.productId, a.id, s.section.id, t.id, t.localized_title);
              if (typeof t.value === 'boolean') {
                this.checked.set(this.checkboxId(t.id, s.section.id, medium), t.value);
              }
            }
          }
        }
      }
      return builder.build();
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public selectedPanelSection$ = new BehaviorSubject<PanelSection>(null);

  formReady$ = combineLatest([
    this.globalSettings$,
    this.panelSections$,
    this.reportSettings$,
    this.settingsByAccountGroupId$,
  ]).pipe(
    tap(() => {
      this.rollbackCheckState = new Map<string, boolean>(this.checked);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public businessName$: Observable<string>;
  public address$: Observable<string>;

  setSelectedPanelSection(panel: PanelSection | null): void {
    this.selectedPanelSection$.next(panel);
  }

  public reset(): void {
    this.checked = new Map<string, boolean>(this.rollbackCheckState);
  }

  public save(): void {
    this.saving = true;
    const settings: SaveSetting[] = [];
    this.checked.forEach((enabled: boolean, id: string) => {
      const pieces = id.split('~');
      const section = pieces[1];
      const setting = pieces[2];

      settings.push(
        new SaveSetting({
          section: section,
          setting: setting,
          valueFlag: enabled,
        }),
      );
    });

    this.notificationsService
      .saveSections$(this.accountGroupId, this.partnerId, settings)
      .pipe(
        take(1),
        catchError(() => {
          this.snackbarService.show(
            this.translate.instant('NOTIFICATIONS.SETTINGS.SNACKBAR.SAVE_FAILURE'),
            this.translate.instant('NOTIFICATIONS.ACTIONS.DISMISS'),
            { duration: 3000 },
          );
          this.checked = new Map<string, boolean>(this.rollbackCheckState);
          this.saving = false;
          return of(null);
        }),
        filter((resp) => !!resp),
      )
      .subscribe(() => {
        this.rollbackCheckState = new Map<string, boolean>(this.checked);
        this.snackbarService.show(this.translate.instant('NOTIFICATIONS.SETTINGS.SNACKBAR.UPDATED'), null, {
          duration: 3000,
        });
        this.saving = false;
      });
  }

  dirty(): boolean {
    let dirty = false;
    this.checked.forEach((v, k) => {
      const untouched = v === this.rollbackCheckState.get(k);
      if (!untouched) {
        dirty = true;
      }
    });
    return dirty;
  }

  isMediumDisabled(enabledMediums: MediumSection[], medium: Medium): boolean {
    const ms = enabledMediums.find((m: MediumSection) => m.medium === medium);
    if (!ms) {
      return true;
    }
    const globalEnabled = this.checked.get(this.checkboxId(ms.id, ms.id, Medium.GLOBAL));
    return !ms || !globalEnabled;
  }

  getDisabledTooltip(enabledMediums: MediumSection[], medium: Medium): string {
    const ms = enabledMediums.find((m: MediumSection) => m.medium === medium);
    if (!ms) {
      return 'NOTIFICATIONS.SETTINGS.UNAVAILABLE';
    }
    if (!this.checked.get(this.checkboxId(ms.id, ms.id, Medium.GLOBAL))) {
      return 'VBC_NOTIFICATIONS.GLOBALLY_DISABLED';
    }
    return 'NOTIFICATIONS.SETTINGS.UNAVAILABLE';
  }

  getMedium(enabledMediums: MediumSection[], medium: Medium): MediumSection {
    return enabledMediums.find((m: MediumSection) => m.medium === medium);
  }

  getMediumFromId(sectionId: string): Medium {
    switch (sectionId) {
      case 'activityDigestEmailFlag':
        return Medium.DIGEST_EMAIL;
      case 'instantEmailFlag':
        return Medium.INSTANT_EMAIL;
      default:
        return null;
    }
  }

  checkboxId(id: string, sectionId: string, medium: Medium): string {
    return `${medium}~${sectionId}~${id}`;
  }

  private getCategoryState(panel: PanelSection): CategoryState {
    const set = new Set();
    panel.activities.forEach((activity: Activity) => {
      const ownProps = Object.keys(activity.enabledMediums.map((m: MediumSection) => m.medium));
      let i = ownProps.length;
      const resArray = new Array(i);
      while (i--) {
        resArray[i] = [ownProps[i], activity.enabledMediums[ownProps[i]]];
      }
      for (const [, medium] of resArray) {
        set.add(this.checked.get(this.checkboxId(activity.id, medium.id, medium.medium)) || false);
      }
      if (activity.tags) {
        activity.tags.forEach((tag: Activity) => {
          for (const [, medium] of resArray) {
            set.add(this.checked.get(this.checkboxId(tag.id, medium.id, medium.medium)) || false);
          }
        });
      }
    });
    if (set.has(true) && set.has(false)) {
      return CategoryState.Indeterminate;
    }
    if (set.has(true)) {
      return CategoryState.On;
    }
    return CategoryState.Off;
  }

  getCategoryIcon(panel: PanelSection): string {
    switch (this.getCategoryState(panel)) {
      case CategoryState.Indeterminate:
        return 'notifications_none';
      case CategoryState.On:
        return 'notifications';
      default:
        return 'notifications_off';
    }
  }

  clickCategoryIcon(panel: PanelSection, event: Event): void {
    event.stopPropagation();
    const catState = this.getCategoryState(panel);
    if (catState === CategoryState.On) {
      this.setEntireCategory(panel, false);
    } else if (catState === CategoryState.Off) {
      this.setEntireCategory(panel, true);
    } else {
      this.setEntireCategory(panel, false);
    }
  }

  getCategoryEnabledState(panel: PanelSection): string {
    switch (this.getCategoryState(panel)) {
      case CategoryState.Indeterminate:
        return 'NOTIFICATIONS.SETTINGS.DESCRIPTION.SOME_ON';
      case CategoryState.On:
        return 'NOTIFICATIONS.SETTINGS.DESCRIPTION.ALL_ON';
      default:
        return 'NOTIFICATIONS.SETTINGS.DESCRIPTION.ALL_OFF';
    }
  }

  setEntireCategory(panel: PanelSection, toggleState: boolean): void {
    panel.activities.forEach((activity: Activity) => {
      this.setActivityState(activity, activity.enabledMediums, toggleState);
    });
  }

  private setActivityState(activity: Activity, mediums: MediumSection[], toggleState: boolean): void {
    if (mediums) {
      mediums.forEach((medium: MediumSection) => {
        this.checked.set(this.checkboxId(activity.id, medium.id, medium.medium), toggleState);
      });
    }
    if (activity.tags) {
      activity.tags.forEach((tag: Activity) => {
        this.setActivityState(tag, mediums, toggleState);
      });
    }
  }

  getTranslationKeyForMedium(medium: Medium): string {
    switch (medium) {
      case Medium.IN_APP:
        return 'VBC_NOTIFICATIONS.MEDIUMS.IN_APP';
      case Medium.INSTANT_EMAIL:
        return 'VBC_NOTIFICATIONS.MEDIUMS.INSTANT_EMAIL';
      case Medium.DIGEST_EMAIL:
        return 'VBC_NOTIFICATIONS.MEDIUMS.DIGEST_EMAIL';
      case Medium.WEEKLY_EMAIL:
        return 'VBC_NOTIFICATIONS.MEDIUMS.WEEKLY_EMAIL';
      case Medium.MONTHLY_EMAIL:
        return 'VBC_NOTIFICATIONS.MEDIUMS.MONTHLY_EMAIL';
      default:
        return 'NOTIFICATIONS.SETTINGS.MEDIUMS.UNKNOWN';
    }
  }
}
